import { api as client } from './client';

export type Coupon = {
  _id: string;
  partnerType: 'store' | 'vet';
  partnerId?: string;
  partner?: { _id: string; name?: string; role?: string };
  copy?: string;
  title: string;
  description: string;
  discount: string;
  active: boolean;
  sponsored?: boolean;
  sponsorshipStatus?: 'none' | 'pending' | 'active';
  targetAnimalCode?: string | null;
  bonusPatitas?: number | null;
  /** Patitas que da de verdad (las suyas o las de por defecto del servidor). */
  rewardPatitas?: number;
  /** Lo que cuesta al comercio cada uso (Patitas a su valor + margen). */
  costEur?: number;
  expiresAt?: string | null;
  usedAt?: string | null;
};

export type SponsorResult = {
  status: 'active' | 'pending';
  sponsored?: boolean;
  alreadyActive?: boolean;
  configured?: boolean;
  url?: string | null;
  priceEur?: number;
  message?: string;
};

// El partner dueño (o admin) paga el placement destacado del cupón. Con Stripe
// configurado devuelve la URL de Checkout; si no, queda 'pending' sin cobrar.
export async function sponsorCoupon(couponId: string) {
  const { data } = await client.post(`/api/offers/coupons/${couponId}/sponsor`);
  return data as SponsorResult;
}

// Lo que el partner puede decidir de un cupón suyo. Ni partnerId ni sponsored:
// el primero sale del token en el servidor y el segundo se paga aparte.
export type PartnerCouponInput = {
  title: string;
  description?: string;
  discount: string;
  bonusPatitas?: number;
  targetAnimalCode?: string | null;
  expiresAt?: string | null;
  active?: boolean;
};

export async function listMyCoupons() {
  const { data } = await client.get('/api/coupons/mine');
  return data as { items: Coupon[]; tarifa?: { costePorPatitaEur: number; visita: { patitas: number; costEur: number }; cuponPorDefecto: number } };
}

export async function createMyCoupon(payload: PartnerCouponInput) {
  const { data } = await client.post('/api/coupons/mine', payload);
  return data as Coupon;
}

export async function updateMyCoupon(couponId: string, payload: Partial<PartnerCouponInput>) {
  const { data } = await client.patch(`/api/coupons/mine/${couponId}`, payload);
  return data as Coupon;
}

export async function listCoupons() {
  const { data } = await client.get('/api/coupons');
  return data as { items: Coupon[] };
}

export async function listAvailableCoupons(params: { code: string }) {
  const { data } = await client.get('/api/coupons/available', { params });
  return data as { items: Coupon[] };
}

export async function redeemCoupon(couponId: string, payload: { animalCode: string; logId?: string }) {
  const { data } = await client.post(`/api/coupons/${couponId}/use`, payload);
  return data as { ok: boolean; coupon: Coupon; logId?: string };
}

// Usa un cupón para un cliente identificado, generándole sus Patitas.
// `ref`: el QR (userToken) o el código con que la caja identificó al cliente;
// el servidor ya no acepta un userId suelto (AUD-3).
export async function applyCouponToCustomer(couponId: string, ref: { userToken?: string; code?: string }, animalCode?: string) {
  const { data } = await client.post(`/api/coupons/${couponId}/use`, { ...ref, animalCode }, { skipErrorToast: true } as any);
  return data as { ok: boolean; coupon: Coupon; earn?: { earned: number; autoDonated: boolean } };
}
